import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Form, Select, Tooltip } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import WelcomeHeader from 'components/layout/WelcomeHeader/index'
import ButtonBlack from 'components/design/Form/ButtonBlack/index'
import style from './style.module.scss'
import FormInput from '../../../components/design/Form/Input'
import FormRadioGroup from '../../../components/design/Form/RadioGroup'
import FormRadioButton from '../../../components/design/Form/RadioButton'
import RegionUSIcon from '../../../assets/cometchat/images/region-us.svg'
import RegionEUIcon from '../../../assets/cometchat/images/region-eu.svg'
import RegionINIcon from '../../../assets/cometchat/images/region-in.svg'
import reactIcon from '../../../assets/cometchat/images/react_small.png'
import reactRouterIcon from '../../../assets/cometchat/images/react-router.png'
import nextIcon from '../../../assets/cometchat/images/nextjs_small.png'
import angularIcon from '../../../assets/cometchat/images/angular_small.png'
import vueIcon from '../../../assets/cometchat/images/vue_small.png'
import androidIcon from '../../../assets/cometchat/images/android_small.png'
import iosIcon from '../../../assets/cometchat/images/ios_small.png'
import ionicIcon from '../../../assets/cometchat/images/ionic_small.png'
import flutterIcon from '../../../assets/cometchat/images/flutter_small.png'
import wordpressIcon from '../../../assets/cometchat/images/wordpress_small.png'
import htmlIcon from '../../../assets/cometchat/images/html_small.png'
import phpIcon from '../../../assets/cometchat/images/php_round.png'
import laravelIcon from '../../../assets/cometchat/images/laravel_small.png'
import jsIcon from '../../../assets/cometchat/images/javascript_small.png'
import webFlowIcon from '../../../assets/cometchat/images/webflow_icon.svg'
import shopifyIcon from '../../../assets/cometchat/images/shopify_icon.svg'
import wixIcon from '../../../assets/cometchat/images/wix_icon.svg'
import squarespaceIcon from '../../../assets/cometchat/images/squarespace_icon.svg'
import otherIcon from '../../../assets/cometchat/images/other_icon.png'
import constants from '../../../constants'
import './index.css'
import StepChooseFeatures from './ChooseFeatures'

const CreateApplication = () => {
  const [form] = Form.useForm()
  const [applicationName, setApplicationName] = useState('My chat app')
  const [region, setRegion] = useState('us')
  const [appTechnology, setAppTechnology] = useState('')
  const [appIndustry, setAppIndustry] = useState('')
  const [showOtherTechnology, setShowOtherTechnology] = useState(false)
  const [otherTechnology, setOtherTechnology] = useState('')
  const [activeTab, setActiveTab] = useState('code')
  const [currentStep, setCurrentStep] = useState(3)
  const [selectedFeatures, setSelectedFeatures] = useState([])

  const apps = useSelector(state => state.apps)
  const user = useSelector(state => state.user)
  const radioLabelTextStyles = { padding: '0px', color: '#434548', width: '100%' }

  const dispatch = useDispatch()

  useEffect(() => {
    if (user.meta !== undefined && user.meta.nearestDataCenter) {
      setRegion(user.meta.nearestDataCenter)
      form.setFieldsValue({ region: user.meta.nearestDataCenter })
    } else if (user.id && user.id !== '') {
      setRegion('us')
      form.setFieldsValue({ region: 'us' })
    }
  }, [user, form])

  const handleApplicationNameInput = e => {
    setApplicationName(e.target.value)
  }

  const handleRegionInput = e => {
    setRegion(e.target.value)
  }

  const getTechnologyType = (technology, tab) => {
    if (technology === 'php' || technology === 'laravel') {
      return 'no-code'
    }
    return tab === 'code' ? 'code' : 'no-code'
  }

  const handleAfterAppCreation = ({ error }) => {
    console.log('error', error)

    if (!error) {
      // create app
      if (window.location.pathname === '/create-your-first-application') {
        const payload = {
          name: applicationName,
          region,
          version: '3',
          metadata: {
            technology: appTechnology,
            industry: appIndustry,
            otherTechnology,
            technologyType: getTechnologyType(appTechnology, activeTab),
            features: selectedFeatures,
          },
          source: 'onboarding',
        }

        dispatch({
          type: 'apps/ADD_APP_ON_ONBOARDING',
          payload,
          onComplete: handlePostFirstAppCreation,
        })
      } else if (window.location.pathname === '/create-application') {
        const payload = {
          name: applicationName,
          region,
          version: '3',
          metadata: {
            technology: appTechnology,
            industry: appIndustry,
            otherTechnology,
            technologyType: getTechnologyType(appTechnology, activeTab),
            features: selectedFeatures,
          },
        }
        dispatch({
          type: 'apps/ADD',
          payload,
          onComplete: handlePostAppCreation,
        })
      }
    }
  }

  const handlePostAppCreation = ({ error }) => {
    if (!error) {
      if (region !== 'hipaa-us') {
        dispatch({
          type: 'apps/UPDATE_ACTIVE_APP',
          payload: {
            id: apps.appCreated.id,
            callSource: 'appCard',
            callSourceRedirectURL: `/app/${apps.appCreated.id}/overview`,
          },
        })
        if (constants.captureGTMEvents && window.CometChatAnalytics) {
          window.CometChatAnalytics.event(constants.GAEvents.app_creation)
        }
      }
    }
  }

  const handlePostFirstAppCreation = ({ error, data }) => {
    if (!error && data.data) {
      dispatch({
        type: 'apps/UPDATE_ACTIVE_APP',
        payload: {
          id: data.data.id,
          callSource: 'appCard',
        },
      })
      if (constants.captureGTMEvents && window.CometChatAnalytics) {
        window.CometChatAnalytics.event(constants.GAEvents.app_creation)
      }
    }
  }

  const handleTechnologyChange = selectedTechnology => {
    setAppTechnology(selectedTechnology.target.value)
    form.setFieldsValue({ otherTechnology: '' })
    if (selectedTechnology.target.value === 'other') {
      setShowOtherTechnology(true)
    } else {
      setShowOtherTechnology(false)
    }
  }

  const handleOtherTechnologyInput = e => {
    setOtherTechnology(e.target.value)
  }

  const handleIndustryChange = selectedIndustry => {
    setAppIndustry(selectedIndustry)
  }

  const buildInitialValues = {
    applicationName,
    region,
    otherTechnology,
  }

  const TechnologiesComponent = ({ code }) => {
    const renderRadioLabel = (text, value) => (
      <div className={style.radioContent}>
        {text}
        <div className={appTechnology === value ? style.radioIconSelected : style.radioIcon} />
      </div>
    )

    return code ? (
      <FormRadioGroup
        formItemStyles={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
        }}
        onChange={handleTechnologyChange}
        Form={Form}
        name="technology"
        value={appTechnology}
        rules={[{ required: true, message: 'Please select your technology' }]}
      >
        <FormRadioButton
          radioLabelTextStyles={radioLabelTextStyles}
          icon={<img src={reactIcon} alt="React" />}
          label={renderRadioLabel('React', 'react')}
          value="react"
          alt="React"
        />
        <FormRadioButton
          radioLabelTextStyles={radioLabelTextStyles}
          icon={<img src={nextIcon} alt="Next JS" />}
          label={renderRadioLabel('Next JS', 'next')}
          value="next"
          alt="Next JS"
        />
        <FormRadioButton
          radioLabelTextStyles={radioLabelTextStyles}
          icon={<img src={reactIcon} alt="React Native" />}
          label={renderRadioLabel('React Native', 'react_native')}
          value="react_native"
          alt="React Native"
        />
        <FormRadioButton
          radioLabelTextStyles={radioLabelTextStyles}
          icon={<img src={reactRouterIcon} alt="React Router" />}
          label={renderRadioLabel('React Router', 'react_router')}
          value="react_router"
          alt="React Router"
        />
        <FormRadioButton
          radioLabelTextStyles={radioLabelTextStyles}
          icon={<img src={flutterIcon} alt="Flutter" />}
          label={renderRadioLabel('Flutter', 'flutter')}
          value="flutter"
          alt="Flutter"
        />
        <FormRadioButton
          radioLabelTextStyles={radioLabelTextStyles}
          icon={<img src={iosIcon} alt="iOS" />}
          label={renderRadioLabel('iOS', 'ios')}
          value="ios"
          alt="iOS"
        />
        <FormRadioButton
          radioLabelTextStyles={radioLabelTextStyles}
          icon={<img src={androidIcon} alt="Android" />}
          label={renderRadioLabel('Android', 'android')}
          value="android"
          alt="Android"
        />
        <FormRadioButton
          radioLabelTextStyles={radioLabelTextStyles}
          icon={<img src={angularIcon} alt="Angular" />}
          label={renderRadioLabel('Angular', 'angular')}
          value="angular"
          alt="Angular"
        />
        <FormRadioButton
          radioLabelTextStyles={radioLabelTextStyles}
          icon={<img src={vueIcon} alt="Vue" />}
          label={renderRadioLabel('Vue', 'vue')}
          value="vue"
          alt="Vue"
        />
        <FormRadioButton
          radioLabelTextStyles={radioLabelTextStyles}
          icon={<img src={jsIcon} alt="Javascript" />}
          label={renderRadioLabel('Javascript', 'javascript')}
          value="javascript"
          alt="JavaScript"
        />
        <FormRadioButton
          radioLabelTextStyles={radioLabelTextStyles}
          icon={<img src={ionicIcon} alt="Ionic Capacitor" />}
          label={renderRadioLabel('Ionic Capacitor', 'ionic_capacitor')}
          value="ionic_capacitor"
          alt="Ionic Capacitor"
        />
        <FormRadioButton
          radioLabelTextStyles={radioLabelTextStyles}
          icon={<img src={phpIcon} alt="PHP" style={{ height: '18px', width: '32px' }} />}
          label={renderRadioLabel('PHP', 'php')}
          value="php"
          alt="PHP"
        />
        <FormRadioButton
          radioLabelTextStyles={radioLabelTextStyles}
          icon={<img src={laravelIcon} alt="Laravel" />}
          label={renderRadioLabel('Laravel', 'laravel')}
          value="laravel"
          alt="Laravel"
        />
        <FormRadioButton
          radioLabelTextStyles={radioLabelTextStyles}
          icon={<img src={otherIcon} alt="Others" />}
          label={renderRadioLabel('Other', 'other')}
          value="other"
          alt="Others"
        />
      </FormRadioGroup>
    ) : (
      <FormRadioGroup
        formItemStyles={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
        }}
        onChange={handleTechnologyChange}
        Form={Form}
        name="technology"
        value={appTechnology}
        rules={[{ required: true, message: 'Please select your technology' }]}
      >
        <FormRadioButton
          radioLabelTextStyles={radioLabelTextStyles}
          icon={<img src={wordpressIcon} alt="WordPress" />}
          label={renderRadioLabel('WordPress', 'wordpress')}
          value="wordpress"
          alt="WordPress"
        />
        <FormRadioButton
          radioLabelTextStyles={radioLabelTextStyles}
          icon={<img src={htmlIcon} alt="HTML" />}
          label={renderRadioLabel('HTML', 'html')}
          value="html"
          alt="HTML"
        />
        <FormRadioButton
          radioLabelTextStyles={radioLabelTextStyles}
          icon={<img src={webFlowIcon} alt="Webflow" />}
          label={renderRadioLabel('Webflow', 'webflow')}
          value="webflow"
          alt="Webflow"
        />

        <FormRadioButton
          radioLabelTextStyles={radioLabelTextStyles}
          icon={<img src={shopifyIcon} alt="Shopify" />}
          label={renderRadioLabel('Shopify', 'shopify')}
          value="shopify"
          alt="Shopify"
        />
        <FormRadioButton
          radioLabelTextStyles={radioLabelTextStyles}
          icon={<img src={wixIcon} alt="WIX" />}
          label={renderRadioLabel('WIX', 'wix')}
          value="wix"
          alt="WIX"
        />
        <FormRadioButton
          radioLabelTextStyles={radioLabelTextStyles}
          icon={<img src={squarespaceIcon} alt="Squarespace" />}
          label={renderRadioLabel('Squarespace', 'squarespace')}
          value="squarespace"
          alt="Squarespace"
        />
        <FormRadioButton
          radioLabelTextStyles={radioLabelTextStyles}
          icon={<img src={otherIcon} alt="Others" />}
          label={renderRadioLabel('Other', 'other')}
          value="other"
          alt="Others"
        />
      </FormRadioGroup>
    )
  }

  const nextStep = () => {
    setCurrentStep(prev => prev + 1)
  }

  const handleContinue = async () => {
    try {
      await form.validateFields(['applicationName', 'industry', 'region'])
      nextStep() // move to next step if valid
    } catch (errorInfo) {
      // validation failed -> antd will automatically show error messages
      console.log('Validation Failed:', errorInfo)
    }
  }

  const createApplication = (
    <>
      <div className="onboarding-create-app-wrapper create-application-container">
        <div className="create-application">
          <div className="create-application-header" style={{ paddingBottom: '24px' }}>
            <div className="create-application-header-title">Create Application</div>
            <div className="create-application-header-subtitle">
              Provide a details about your app to help us configure it correctly
            </div>
          </div>
          <div className="create-application-sub-header">
            <div className="create-application-form-item">
              <FormInput
                formItemStyles={{ marginBottom: '24px' }}
                style={{ height: '44px', borderColor: '#d5d7da' }}
                onChange={handleApplicationNameInput}
                Form={Form}
                label={<div className={style.detailLabel}>Application Name</div>}
                name="applicationName"
                value={applicationName}
                placeholder="Your CometChat application name"
                rules={[{ required: true, message: 'Please enter your application name' }]}
              />
            </div>

            <div className="create-application-form-item">
              <div className={style.detailLabel1}>Industry</div>
              <Form.Item
                required
                name="industry"
                placeholder="Select your industry"
                Form={Form}
                rules={[
                  {
                    required: true,
                    message: 'Please select your industry',
                  },
                ]}
              >
                <Select
                  placeholder="Select your industry"
                  onChange={handleIndustryChange}
                  className={style.selectOption}
                >
                  <Select.Option value="online_marketplaces">Online Marketplaces</Select.Option>
                  <Select.Option value="healthcare">Healthcare</Select.Option>{' '}
                  <Select.Option value="dating">Dating</Select.Option>
                  <Select.Option value="events_and_streaming">
                    Events & Streaming
                  </Select.Option>{' '}
                  <Select.Option value="online_education">Online Education</Select.Option>
                  <Select.Option value="community_and_social">Community & Social</Select.Option>
                  <Select.Option value="saas_businesses">SaaS & Multi-Tenant</Select.Option>{' '}
                  <Select.Option value="sports_and_gaming">Sports & Gaming</Select.Option>
                  <Select.Option value="team_comms_and_workflows">
                    Team Comms & Workflows
                  </Select.Option>
                  <Select.Option value="on_demand_services">On-Demand Services</Select.Option>
                </Select>
              </Form.Item>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '4px' }}>
              <div className={style.detailLabel}>
                Region
                <Tooltip
                  title={
                    <span style={{ textAlign: 'center' }} className={style.tooltipLabel}>
                      This is the region where your CometChat data will be stored. Users can connect
                      and chat globally.
                    </span>
                  }
                  placement="top"
                  overlayStyle={{ maxWidth: 265 }}
                >
                  <InfoCircleOutlined style={{ color: '#A4A7AE' }} />
                </Tooltip>
              </div>
              <FormRadioGroup
                formItemStyles={{ display: 'flex', width: '100%', marginBottom: '0px' }}
                onChange={handleRegionInput}
                Form={Form}
                name="region"
                value={region}
                rules={[{ required: true, message: 'Please select your application region' }]}
              >
                <FormRadioButton
                  radioLabelTextStyles={{ ...radioLabelTextStyles, width: 'fit-content' }}
                  radioButtonStyles={{ height: '44px', padding: '0px', flex: '1' }}
                  icon={<img src={RegionUSIcon} alt="region US" />}
                  label="US"
                  value="us"
                  alt="US"
                />
                <FormRadioButton
                  radioLabelTextStyles={{ ...radioLabelTextStyles, width: 'fit-content' }}
                  radioButtonStyles={{ height: '44px', padding: '0px', flex: '1' }}
                  icon={<img src={RegionEUIcon} alt="region EU" />}
                  label="EU"
                  value="eu"
                  alt="eu"
                />
                <FormRadioButton
                  radioLabelTextStyles={{ ...radioLabelTextStyles, width: 'fit-content' }}
                  radioButtonStyles={{ height: '44px', padding: '0px', flex: '1', margin: 0 }}
                  icon={<img src={RegionINIcon} alt="region IN" />}
                  label="IN"
                  value="in"
                  alt="in"
                />
              </FormRadioGroup>
            </div>
          </div>

          <ButtonBlack
            buttonStyles={{
              width: '100%',
              height: '44px',
              marginTop: '24px',
              fontSize: '16px',
            }}
            holderStyles={{ width: '100%' }}
            buttonText="Continue"
            loading={apps.loading}
            onClick={handleContinue}
          />
        </div>
      </div>
    </>
  )
  const choosePlatform = (
    <div className="create-application-container choose-platform-wrapper">
      <div className="create-application choose-platform">
        <div className="create-application-header" style={{ paddingBottom: '24px' }}>
          <div className="choose-platform-title"> Choose Your Platform </div>
          <div className="choose-platform-subtitle">
            {' '}
            Select your preferred platform or framework. You can add or switch to another technology
            later
          </div>
        </div>

        <div className="create-application-technology-tabs" style={{ marginBottom: '24px' }}>
          <div
            className={
              activeTab === 'code'
                ? 'create-application-technology-tabs-item-active'
                : 'create-application-technology-tabs-item'
            }
            onClick={() => setActiveTab('code')}
            tabIndex={0}
            onKeyDown={() => setActiveTab('code')}
            role="button"
          >
            Code
          </div>
          <div
            className={
              activeTab === 'no-code'
                ? 'create-application-technology-tabs-item-active'
                : 'create-application-technology-tabs-item'
            }
            onClick={() => setActiveTab('no-code')}
            tabIndex={0}
            onKeyDown={() => setActiveTab('no-code')}
            role="button"
          >
            No Code
          </div>
        </div>
        <div className="tabsWrapper">
          {activeTab === 'code' ? (
            <TechnologiesComponent code />
          ) : (
            <TechnologiesComponent code={false} />
          )}
        </div>

        {showOtherTechnology && (
          <FormInput
            onChange={handleOtherTechnologyInput}
            Form={Form}
            label="Enter your framework or platform"
            name="otherTechnology"
            value={otherTechnology}
            placeholder="e.g., Svelte, Astro, Expo"
            rules={[{ required: true, message: 'Please Type your framework or platform' }]}
            formItemStyles={{ with: '100%' }}
          />
        )}
        <ButtonBlack
          buttonStyles={{
            width: '100%',
            height: '44px',
            marginTop: '24px',
            fontSize: '16px',
          }}
          holderStyles={{ width: '100%' }}
          buttonText="Get Started"
          buttonType="submit"
          loading={apps.loading}
        />
      </div>
    </div>
  )

  function prevStep() {
    setCurrentStep(prev => prev - 1)
  }

  return (
    <>
      <WelcomeHeader
        step={window.location.pathname.includes('create-your-first-app') ? currentStep : null}
        totalSteps={6}
      />
      <div className="createAppWrapper">
        <div className="create-application-form1">
          <div style={{ padding: '20px 0 0 20px', height: '56px' }}>
            {currentStep > 3 && (
              <div
                className="onBoardingBackButtonWrapper"
                role="button"
                tabIndex={0}
                onClick={prevStep}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    prevStep()
                  }
                }}
                aria-label="Go to previous step"
              >
                <div className="onBoardingBackButton" aria-hidden="true" />
              </div>
            )}
          </div>
          <Form
            form={form}
            style={{ width: '100%', padding: '10px' }}
            hideRequiredMark
            onFinish={handleAfterAppCreation}
            colon={false}
            initialValues={buildInitialValues}
          >
            {currentStep === 3 && createApplication}
            {currentStep === 4 && (
              <StepChooseFeatures
                nextStep={nextStep}
                selectedFeatures={selectedFeatures}
                setSelectedFeatures={setSelectedFeatures}
                form={form}
              />
            )}
            {currentStep === 5 && choosePlatform}
          </Form>
        </div>
      </div>
    </>
  )
}

export default CreateApplication
